/**
 * Auto-Fabricator (M3) — runs the workshop hands-off.
 *
 * The selection is in `autoFabricator-core.js`, which is pure and headless.
 * This file is the part that touches the game: it calls the same crafting
 * functions the player's own buttons call, so an automated craft and a manual
 * one are the same event — same marks, same notifications, same save.
 *
 * SEPARATE FROM AUTO-RAID ON PURPOSE. Auto-Fabricator is its own Command
 * upgrade and a player can own it without owning Auto-Raid Protocol, so it gets
 * its own tick rather than a step inside the raid cycle.
 *
 * ONE CRAFT PER TICK, whenever it is switched on — see `autoFabricatorTick` for
 * why it does not wait for an "idle" window, which is a mistake this shipped
 * with for a day.
 */

import { gameState, saveGameState } from './gameState.js';
import { craftItem, unlockArtifact } from './crafting.js';
import { nextFabrication, isAutoFabricatorOwned, AUTO_FABRICATOR_ID } from './autoFabricator-core.js';
import { debug, CH } from './debugLog.js';

export { isAutoFabricatorOwned, AUTO_FABRICATOR_ID };

export function isAutoFabricatorEnabled() {
    return isAutoFabricatorOwned() && gameState.settings?.autoFabricatorEnabled === true;
}

/**
 * What the last tick did — null when it found nothing worth building.
 *
 * Kept so the button can tell "on and working" from "on with nothing to build".
 * Not persisted: it describes this session's last tick, and a stale verdict read
 * from a save would be worse than none.
 */
let lastJob = null;
let hasTicked = false;

export function setAutoFabricatorEnabled(enabled) {
    if (!gameState.settings) gameState.settings = {};
    gameState.settings.autoFabricatorEnabled = !!enabled;
    // A fresh switch-on has not decided anything yet, so the button must not
    // show a verdict left over from the last time it ran.
    hasTicked = false;
    lastJob = null;
    saveGameState();
    return gameState.settings.autoFabricatorEnabled;
}

/**
 * One value describing what the player should be told, so the button is a pure
 * renderer and this module stays the only thing that decides.
 *
 * `nothing` is the state that matters. An automation that is on, unblocked and
 * silently doing nothing — because the character holds no blueprint, no full
 * recipe and no affordable component — is indistinguishable from one that is
 * broken. It is also the most common state early on.
 *
 * @returns {{state:'unowned'|'off'|'blocked'|'nothing'|'working', reason?:string}}
 */
export function autoFabricatorStatus() {
    if (!isAutoFabricatorOwned()) return { state: 'unowned' };
    if (!isAutoFabricatorEnabled()) return { state: 'off' };
    const blocked = autoFabricatorBlockedReason();
    if (blocked) return { state: 'blocked', reason: blocked.text };
    if (hasTicked && !lastJob) return { state: 'nothing' };
    return { state: 'working' };
}

/**
 * Why the workshop cannot run right now, or null when it can.
 * Mirrors `autoRaidBlockedReason`: a toggle that silently does nothing is worse
 * than no toggle.
 */
export function autoFabricatorBlockedReason() {
    const character = gameState.character;
    if (!character) return { code: 'no-character', text: 'No active character' };
    // The same refusal `craftItem` makes, checked here so the automation never
    // calls into a path that would raise an error notification at the player.
    if (character.isMarketplace || character.marketplaceData) {
        return { code: 'listed', text: 'Character is listed on the marketplace' };
    }
    return null;
}

/**
 * Perform at most one fabrication. Returns what it did, or null.
 *
 * Records the outcome for `autoFabricatorStatus`, so "found nothing to build"
 * is a state the player can see rather than an absence they have to infer.
 */
export function fabricateStep() {
    if (!isAutoFabricatorEnabled() || autoFabricatorBlockedReason()) return null;

    const job = nextFabrication();
    hasTicked = true;
    lastJob = job;
    if (!job) return null;

    // The player's own crafting functions, deliberately: they bank the marks,
    // charge the credits, and notify. An automated craft the player never sees
    // in the log would read as the game inventing items.
    const done = job.kind === 'unlock'
        ? unlockArtifact(job.id)
        : craftItem(job.id, job.kind === 'artifact');

    if (!done) {
        // The selector said yes and the craft said no — a real disagreement,
        // worth a debug line rather than a silent retry loop next tick.
        debug(CH.ui, `Auto-Fabricator: ${job.kind} ${job.id} was selected but refused`);
        lastJob = null;
        return null;
    }
    debug(CH.ui, `Auto-Fabricator: ${job.kind} ${job.id}`);
    return job;
}

/**
 * Tick entry point: one craft per tick, whenever the upgrade is on.
 *
 * ⚠ THIS DELIBERATELY HAS NO IDLE WINDOW, and it had one for a day. The first
 * cut only fabricated while a raid was running or the raid cycle was waiting on
 * energy, which meant a player who bought the upgrade, switched it on and
 * watched the character screen saw NOTHING happen — against copy that promises
 * crafting "between raids".
 *
 * The window came from an earlier design where fabrication was a STEP INSIDE
 * `autoRaidTick`, where a deep component tree really would have delayed each
 * launch by a tick per component. As its own tick it shares nothing with the
 * raid cycle and can never delay it, so the constraint bought nothing and cost
 * the feature.
 */
export function autoFabricatorTick() {
    return fabricateStep();
}
